import { useMemo } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { MarketingAsset } from "@/bryntum-lib/models/MarketingAsset";
import { MarketingTag, MarketingTagType } from "@/bryntum-lib/models/MarketingTag";
import { BoardFile } from "@/bryntum-lib/models/BoardFile";
import { EntityRecord } from "@/types/entity";

interface TemporalGroup {
  tag: MarketingTag;
  assets: MarketingAsset[];
}

interface FolderGroup {
  tag: MarketingTag;
  assets: MarketingAsset[];
}

export interface MarketingAssetsData {
  assets: MarketingAsset[];
  temporalGroups: TemporalGroup[];
  folderGroups: FolderGroup[];
  totalApproved: number;
  allTags: MarketingTag[];
}

export const useMarketingAssets = (entityRecord: EntityRecord): MarketingAssetsData => {
  const { project } = useSwivuBryntumContext();

  const { version: assetVersion } = useSwivuStore<MarketingAsset>({ storeId: STORE_KEYS.marketingAssetStore });
  const { version: tagVersion } = useSwivuStore<MarketingTag>({ storeId: STORE_KEYS.marketingTagStore });
  const { version: fileVersion } = useSwivuStore<BoardFile>({ storeId: STORE_KEYS.boardFilesStore });

  return useMemo(() => {
    if (!project) {
      return { assets: [], temporalGroups: [], folderGroups: [], totalApproved: 0, allTags: [] };
    }

    const allTags = (project.marketingTagStore?.query(() => true, true) ?? []) as MarketingTag[];
    const allAssets = (project.marketingAssetStore?.query(() => true, true) ?? []) as MarketingAsset[];
    const allFiles = (project.boardFilesStore?.query(() => true, true) ?? []) as BoardFile[];

    const entityFileIds = new Set(
      allFiles
        .filter((f: any) => f[entityRecord.entityType] === entityRecord.id)
        .map((f: any) => f.id)
    );

    const entityAssets = allAssets.filter((a) => entityFileIds.has(a.file));

    const temporalTags = allTags.filter((t) => t.type === MarketingTagType.TEMPORAL);
    const folderTags = allTags.filter((t) => t.type === MarketingTagType.CONTENT || t.isFolderEligible);

    const temporalGroups: TemporalGroup[] = temporalTags.map((tag) => ({
      tag,
      assets: entityAssets.filter((a) => a.tags?.includes(tag.id)),
    })).filter((g) => g.assets.length > 0);

    const folderGroups: FolderGroup[] = folderTags.map((tag) => ({
      tag,
      assets: entityAssets.filter((a) => a.tags?.includes(tag.id)),
    })).filter((g) => g.assets.length > 0);

    return {
      assets: entityAssets,
      temporalGroups,
      folderGroups,
      totalApproved: entityAssets.length,
      allTags,
    };
  }, [project, entityRecord, assetVersion, tagVersion, fileVersion]);
};
